import * as React from 'react';
import { LuArchive, LuArchiveRestore, LuCheck, LuChevronDown, LuHistory, LuMessageSquarePlus, LuPencil, LuTrash2, LuX } from 'react-icons/lu';
import { AccentSpinner } from '../AccentSpinner';
import { t } from '../../services/HelperService';
import {
  SESSION_STATUS_ACTIVE,
  SESSION_STATUS_ARCHIVED,
  deleteAssistantSession,
  listAssistantSessions,
  renameAssistantSession,
  setAssistantSessionStatus,
} from './assistantSessionApi';

const safeString = (v) => (v ?? '').toString();

/** "just now" / "5 min ago" / "2 h ago" / "yesterday" / a date — the picker's and header's time. */
export const formatRelativeTime = (date, now = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const diffMs = now.getTime() - date.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return t('just now');
  if (minutes < 60) return t('{{count}} min ago', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('{{count}} h ago', { count: hours });
  const days = Math.round(hours / 24);
  if (days === 1) return t('yesterday');
  if (days < 7) return t('{{count}} days ago', { count: days });
  return date.toLocaleDateString();
};

const fullTime = (date) => (date instanceof Date && !Number.isNaN(date.getTime())
  ? `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  : '');

const iconButtonStyle = (disabled) => ({
  border: 'none',
  background: 'transparent',
  padding: 2,
  lineHeight: 0,
  color: disabled ? '#adb5bd' : '#6c757d',
  cursor: disabled ? 'default' : 'pointer',
  borderRadius: 4,
});

/**
 * One session's row in the picker: title, the last prompt, when and by whom; archive /
 * restore and delete on hover (delete asks twice, inline). The current session is marked.
 */
const SessionRow = ({ session, current, busy, onOpen, onArchiveToggle, onDelete }) => {
  const [hover, setHover] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const archived = session.status === SESSION_STATUS_ARCHIVED;
  const meta = [
    session.turnCount > 0 ? t('{{count}} turns', { count: session.turnCount }) : t('no turns yet'),
    session.updatedOn ? `${formatRelativeTime(session.updatedOn)}${session.updatedBy ? ` · ${session.updatedBy}` : ''}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <div
      data-tagname="sessionRow"
      role="button"
      tabIndex={0}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setConfirmDelete(false); }}
      onClick={() => { if (!busy && !current) onOpen(session); }}
      onKeyDown={(e) => { if (e.key === 'Enter' && !busy && !current) onOpen(session); }}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '7px 10px',
        borderRadius: 6,
        cursor: current || busy ? 'default' : 'pointer',
        background: current ? '#e3f2fd' : (hover ? '#f8f9fa' : 'transparent'),
        opacity: archived && !current ? 0.75 : 1,
      }}
    >
      <div style={{ width: 16, flex: '0 0 16px', paddingTop: 2, color: '#1565c0' }}>
        {current ? <LuCheck size={14} /> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={session.title}>
            {session.title || t('Untitled session')}
          </span>
          {archived && (
            <span style={{ fontSize: 10, color: '#6c757d', border: '1px solid #dee2e6', borderRadius: 8, padding: '0 6px' }}>{t('archived')}</span>
          )}
          {session.turnInProgress && (
            <span style={{ fontSize: 10, color: '#1565c0', display: 'inline-flex', alignItems: 'center', gap: 4 }}><AccentSpinner size="sm" /> {t('working')}</span>
          )}
        </div>
        {session.lastPromptPreview && (
          <div style={{ fontSize: 11, color: '#495057', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={session.lastPromptPreview}>
            {session.lastPromptPreview}
          </div>
        )}
        <div style={{ fontSize: 10, color: '#6c757d' }} title={fullTime(session.updatedOn)}>{meta}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, visibility: hover || confirmDelete ? 'visible' : 'hidden' }}>
        {confirmDelete ? (
          <>
            <button
              type="button"
              data-tagname="confirmDeleteSession"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); onDelete(session); }}
              disabled={busy || session.turnInProgress}
              style={{ ...iconButtonStyle(busy), color: '#c62828', fontSize: 11, lineHeight: '16px', padding: '0 6px' }}
            >
              {t('Delete?')}
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }} style={iconButtonStyle(false)} title={t('Keep')}>
              <LuX size={13} />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              data-tagname="archiveSession"
              onClick={(e) => { e.stopPropagation(); onArchiveToggle(session); }}
              disabled={busy}
              style={iconButtonStyle(busy)}
              title={archived ? t('Restore to the active sessions') : t('Archive - kept out of the way, never purged')}
            >
              {archived ? <LuArchiveRestore size={13} /> : <LuArchive size={13} />}
            </button>
            <button
              type="button"
              data-tagname="deleteSession"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
              disabled={busy || session.turnInProgress}
              style={iconButtonStyle(busy || session.turnInProgress)}
              title={session.turnInProgress ? t('A reply is being written for this session') : t('Delete this session')}
            >
              <LuTrash2 size={13} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * The strip above an assistant transcript when the session is kept on the server: the
 * session's title (click the pencil to rename), its turn count and last update, and a
 * picker of the host's other sessions — open one to continue it, start a new one, archive
 * or delete. Archived sessions are out of the purge's reach and listed under a toggle.
 *
 * Props:
 * - info: the open session (parseSessionInfo shape)
 * - host: the normalised host group (normalizeSessionHost) the picker lists
 * - busy: a turn is running — switching / deleting waits
 * - onSwitch(session): open another session (the dialog reloads it from the server)
 * - onNew(): start a new session in the group
 * - onInfoChange(info): the open session's row changed (rename / status)
 */
export const AssistantSessionHeader = ({ info, host, busy, onSwitch, onNew, onInfoChange }) => {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showArchived, setShowArchived] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState('');
  const [working, setWorking] = React.useState(false);
  const wrapRef = React.useRef(null);
  const renameRef = React.useRef(null);

  const reload = React.useCallback(async (includeArchived) => {
    setLoading(true);
    setError('');
    try {
      setSessions(await listAssistantSessions(host, includeArchived));
    } catch (err) {
      setError(safeString(err?.message) || t('The sessions could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [host]);

  React.useEffect(() => {
    if (open) reload(showArchived);
  }, [open, showArchived, reload]);

  // Outside click / Escape closes the picker (bubble phase, so clicks inside it win).
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  React.useEffect(() => {
    if (renaming) renameRef.current?.select?.();
  }, [renaming]);

  const startRename = () => {
    if (busy) return;
    setDraftTitle(safeString(info?.title));
    setRenaming(true);
  };

  const commitRename = async () => {
    const title = draftTitle.trim();
    setRenaming(false);
    if (!title || title === safeString(info?.title) || !info?.sessionKey) return;
    try {
      const updated = await renameAssistantSession(info.sessionKey, title);
      if (updated) onInfoChange?.(updated);
    } catch {
      // the update's own exception dialog reported it
    }
  };

  const toggleArchive = async (session) => {
    setWorking(true);
    try {
      const next = session.status === SESSION_STATUS_ARCHIVED ? SESSION_STATUS_ACTIVE : SESSION_STATUS_ARCHIVED;
      const updated = await setAssistantSessionStatus(session.sessionKey, next);
      if (updated && updated.sessionKey === info?.sessionKey) onInfoChange?.(updated);
      await reload(showArchived);
    } finally {
      setWorking(false);
    }
  };

  const remove = async (session) => {
    setWorking(true);
    try {
      await deleteAssistantSession(session.sessionKey);
      if (session.sessionKey === info?.sessionKey) {
        // The open session is gone: the dialog continues on the group's newest (or a new one).
        setOpen(false);
        onNew?.({ afterDelete: true });
        return;
      }
      await reload(showArchived);
    } finally {
      setWorking(false);
    }
  };

  const title = safeString(info?.title) || t('Untitled session');
  const archived = info?.status === SESSION_STATUS_ARCHIVED;
  const summary = [
    info?.turnCount > 0 ? t('{{count}} turns', { count: info.turnCount }) : t('new'),
    info?.updatedOn ? `${t('updated')} ${formatRelativeTime(info.updatedOn)}${info.updatedBy ? ` ${t('by')} ${info.updatedBy}` : ''}` : '',
  ].filter(Boolean).join(' · ');
  const disabled = busy || working;

  return (
    <div ref={wrapRef} data-tagname="assistantSessionHeader" style={{ position: 'relative', minWidth: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <button
          type="button"
          data-tagname="sessionPickerToggle"
          onClick={() => setOpen((v) => !v)}
          title={t('Previous sessions of this assistant')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #dee2e6', background: open ? '#e9ecef' : '#fff',
            borderRadius: 14, padding: '2px 8px 2px 6px', fontSize: 11, color: '#495057', cursor: 'pointer', flex: '0 0 auto',
          }}
        >
          <LuHistory size={13} />
          <span>{t('Sessions')}</span>
          <LuChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }} />
        </button>
        {renaming ? (
          <input
            ref={renameRef}
            data-tagname="sessionTitleInput"
            value={draftTitle}
            maxLength={200}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); }
            }}
            style={{ flex: 1, minWidth: 80, fontSize: 13, fontWeight: 700, border: '1px solid #90caf9', borderRadius: 4, padding: '1px 6px' }}
          />
        ) : (
          <div
            style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0, flex: 1 }}
            onDoubleClick={startRename}
          >
            <span data-tagname="sessionTitle" style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={title}>
              {title}
            </span>
            {archived && (
              <span style={{ fontSize: 10, color: '#6c757d', border: '1px solid #dee2e6', borderRadius: 8, padding: '0 6px', flex: '0 0 auto' }}>{t('archived')}</span>
            )}
            <button type="button" data-tagname="renameSession" onClick={startRename} disabled={busy} style={{ ...iconButtonStyle(busy), flex: '0 0 auto' }} title={t('Rename this session')}>
              <LuPencil size={12} />
            </button>
            <span style={{ fontSize: 10, color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={fullTime(info?.updatedOn)}>
              {summary}
            </span>
          </div>
        )}
      </div>

      {open && (
        <div
          data-tagname="sessionPicker"
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, width: 'min(440px, 100%)', minWidth: 280,
            background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', padding: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 6px 6px' }}>
            <button
              type="button"
              data-tagname="newSession"
              disabled={disabled}
              onClick={() => { setOpen(false); onNew?.(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #90caf9', background: '#e3f2fd', color: '#0d47a1',
                borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
              }}
            >
              <LuMessageSquarePlus size={14} /> {t('New session')}
            </button>
            <label style={{ fontSize: 11, color: '#6c757d', display: 'inline-flex', alignItems: 'center', gap: 4, margin: 0, cursor: 'pointer' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} style={{ margin: 0 }} />
              {t('Show archived')}
            </label>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {loading && !sessions && (
              <div style={{ padding: 10, fontSize: 12, color: '#6c757d', display: 'flex', alignItems: 'center', gap: 6 }}><AccentSpinner size="sm" /> {t('Loading...')}</div>
            )}
            {error && <div style={{ padding: 10, fontSize: 12, color: '#c62828' }}>{error}</div>}
            {sessions && sessions.length === 0 && !error && (
              <div style={{ padding: 10, fontSize: 12, color: '#6c757d' }}>{t('No other sessions yet.')}</div>
            )}
            {(sessions ?? []).map((s) => (
              <SessionRow
                key={s.sessionKey}
                session={s}
                current={s.sessionKey === info?.sessionKey}
                busy={disabled}
                onOpen={(session) => { setOpen(false); onSwitch?.(session); }}
                onArchiveToggle={toggleArchive}
                onDelete={remove}
              />
            ))}
          </div>
          <div style={{ padding: '6px 8px 2px', fontSize: 10, color: '#6c757d', borderTop: '1px solid #f1f3f5', marginTop: 4 }}>
            {t('The 10 most recent sessions of each assistant are kept; archive one to keep it longer.')}
          </div>
        </div>
      )}
    </div>
  );
};
